import { negotiateLanguages } from "@fluent/langneg";
import { FluentBundle, FluentResource } from "@fluent/bundle";
import { ReactLocalization } from "@fluent/react";
import { createDebugFtlString } from "byzantine/src/l10n/Debug";
import { customFluentFunctions } from "byzantine/src/l10n/customFluentFunctions";
import { LANG_CHANGE } from "../browserCustomEvents";
import api from "./api";

export const isValidLocale = (locale) =>
  typeof locale === "object" &&
  typeof locale.state === "string" &&
  typeof locale.latest === "string" &&
  locale.state.length > 0 &&
  locale.latest.length > 0;

/**
 * Determines which locales need to be loaded and returns a list
 * of `FluentBundle` instances
 *
 * @param {String} internalName
 * @param {String} productCode three letter Narmi product code
 * @param {String} apiUrl
 * @param {Boolean} addDebugLang when true, a "debug" locale is added to bundles
 * @returns {Object} map of locale codes to `FluentBundle` instances
 */
export const getAllBundles = async (
  internalName,
  s3ImagesBucket,
  productCode,
  apiUrl,
  addDebugLang = false
) => {
  let manifest = {};
  let fluentResources = [];
  let localeCodes = [];
  let ftlStrings = [];
  let defaultFtl = "";
  let hasSettingsLocales = false;
  let settingsLocaleCodes = [];
  let settingsFtlStrings = [];
  let bundlesMap = {};
  let result = {
    error: undefined,
    bundles: bundlesMap,
  };

  // We should fail fast and hard if the manifest can not be fetched.
  // This early exit prevents the `ReactLocalization` (`l10n`) property
  // from receiving any bundles, which in turn throws a warning we can track.
  // When this happens, any `Localized` components or `getString` functions
  // will use the fallback text defined in JSX source.
  try {
    manifest = await api.fetchFtlManifest(internalName, s3ImagesBucket, apiUrl);
  } catch (error) {
    result.error = error;
    return result;
  }

  if (!Object.keys(manifest).includes(productCode)) {
    result.error = `Product "${productCode}" not found in manifest`;
    return result;
  }

  if (Object.keys(manifest).includes("fi")) {
    hasSettingsLocales = true;
  }

  const productLocales = Object.fromEntries(
    Object.entries(manifest[productCode]).filter(([, data]) => isValidLocale(data))
  );

  // Only create bundles for languages that have been enabled.
  // The institution's `en.ftl` should never be treated as disabled,
  // even if we make some mistake in the manifests.
  const enabledLocales = Object.entries(productLocales).reduce(
    (allowedLocales, [localeCode, localeData]) => {
      if (localeData.state === "enabled" || localeCode === "en") {
        allowedLocales[localeCode] = localeData;
      }
      return allowedLocales;
    },
    {}
  );

  localeCodes = [...localeCodes, ...Object.keys(enabledLocales)];

  try {
    ftlStrings = await Promise.all(
      localeCodes.map((localeCode) =>
        api.fetchInstitutionFtl(
          internalName,
          s3ImagesBucket,
          enabledLocales[localeCode].latest,
          apiUrl
        )
      )
    );
  } catch (error) {
    result.error = error;
    return result;
  }

  // in addition to the locales for this specific product,
  // conditionally load `fi` product locales for settings translations.
  // set in language stack as `<langCode>-settings`
  if (hasSettingsLocales) {
    const settingLocales = Object.fromEntries(
      Object.entries(manifest.fi).filter(([, data]) => isValidLocale(data))
    );

    try {
      settingsLocaleCodes = Object.keys(settingLocales).map((lang) => `${lang}-settings`);

      settingsFtlStrings = await Promise.all(
        Object.values(settingLocales).map((localeData) =>
          api.fetchInstitutionFtl(internalName, s3ImagesBucket, localeData.latest, apiUrl)
        )
      );
    } catch (error) {
      result.error = error;
      return result;
    }
  }

  try {
    defaultFtl = await api.fetchDefaultFtl(productCode, apiUrl);
  } catch (error) {
    result.error = error;
    return result;
  }

  // add default locale code and content
  localeCodes.push("en-default");
  ftlStrings.push(defaultFtl);

  // add `fi` locale codes and content
  if (hasSettingsLocales) {
    localeCodes = localeCodes.concat(settingsLocaleCodes);
    ftlStrings = ftlStrings.concat(settingsFtlStrings);
  }

  if (addDebugLang) {
    localeCodes.push("debug");
    ftlStrings.push(createDebugFtlString(defaultFtl));
  }

  // convert FTL strings to FluentResource instances
  fluentResources = ftlStrings.map((str) => new FluentResource(str));

  // populate map of FluentBundle instances by locale code
  fluentResources.forEach((resource, i) => {
    const localeCode = localeCodes[i];
    const bundle = new FluentBundle(localeCode, { functions: customFluentFunctions });
    bundle.addResource(resource);
    bundlesMap[localeCode] = bundle;
  });

  return { ...result, bundles: bundlesMap };
};

/**
 * @param {String} selectedLocale ISO639 two letter locale code
 * @param {Object} bundlesMap FluentBundles instances by locale code
 * @param {Function} parser custom markup parser for ReactLocalization instance
 * @returns {ReactLocalization}
 */
export const getReactLocalization = (selectedLocale, bundlesMap) => {
  const availableLocales = Object.keys(bundlesMap).filter((code) => code !== "en-default");
  let requestedLocales = [];

  if (selectedLocale) {
    requestedLocales.push(selectedLocale);
  }

  if (navigator && navigator.languages) {
    requestedLocales = [...requestedLocales, navigator.languages];
  }

  // `negotiateLanguages` resolves a list of locale codes in order, based on
  // language selection and user system preferences.
  //
  // If none of the requested locale(s) are found, we fall back to the
  // institution-specific English messages.
  const negotatedLocales = negotiateLanguages(requestedLocales, availableLocales, {
    defaultLocale: "en",
  });

  //`en-default` should always be present as the final locale in the stack
  let localeStack = [...negotatedLocales, "en-default"];

  if (selectedLocale === "debug") {
    localeStack = ["debug"];
  }

  const bundles = localeStack.map((localeCode) => bundlesMap[localeCode]);

  const l10n = new ReactLocalization(bundles);
  window.dispatchEvent(
    new CustomEvent(LANG_CHANGE, {
      detail: {
        l10n,
        bundlesMap,
      },
    })
  );

  return l10n;
};
